import {
  Box,
  Drawer,
  DrawerHeader,
  Hidden,
  Navigation,
} from '@cambridgeassessment/cambridge-ui';
import {
  NavigationItem,
  NavigationSubItem,
} from '@cambridgeassessment/cambridge-ui/build/components/Navigation';
import {
  AccountCircleOutlined,
  HomeOutlined,
  SchoolOutlined,
} from '@material-ui/icons';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { hot } from 'react-hot-loader/root';
import { Link, useLocation } from 'react-router-dom';
import useWindowDimensions from 'src/hooks/useWindowDimensions';
import { GACategory } from 'src/types';
import { sendGAEvent } from 'src/utils/googleAnalytics';
import useAuth from '../../hooks/useAuth';
import { useAppContext } from '../AppContext';
import { routes } from '../Pages';

interface ISidebar {
  show: boolean;
  hideSidebar: () => void;
  className?: string;
}

const Sidebar: React.FC<ISidebar> = ({ show, hideSidebar, className }) => {
  const { width } = useWindowDimensions();
  const { appState, setAppState } = useAppContext();
  const { isDelegate, user } = useAuth();
  const ref = React.useRef(null);
  const location = useLocation();
  const currentTabPath = `/${location.pathname.split('/')[1]}`;
  const currentDropdownItemPath = `/${location.pathname.split('/')[2]}`;

  const [showSuboptions, setShowSuboptions] = useState(currentTabPath);

  const OPTIONS = [
    {
      Icon: HomeOutlined,
      page: routes.Home,
    },
    {
      Icon: AccountCircleOutlined,
      page: routes.Account,
      subOptions: [
        routes.PersonalInfo,
        routes.Communications,
        routes.ConnectedAccounts,
      ],
    },
    isDelegate && {
      Icon: SchoolOutlined,
      page: routes.MyOrganisation,
    },
  ].filter((e) => e);

  const currentTabSelection = useCallback(
    (tabPath: string, dropDownItemPath: string) => {
      return {
        currentTab: OPTIONS.findIndex((x) => x.page.path === tabPath),
        currentDropdownItem: OPTIONS[1].subOptions.findIndex(
          (x) => x.path === `${tabPath}${dropDownItemPath}`
        ),
      };
    },
    []
  );

  const [currentNavItem, setCurrentNavItem] = useState<{
    currentTab?: number;
    currentDropdownItem?: number;
  }>(currentTabSelection(currentTabPath, currentDropdownItemPath));

  useEffect(() => {
    window.onpopstate = () => {
      SetCurrentItem();
    };
  }, []);

  useEffect(() => {
    const presentDropdownItemPath = window.location.pathname.split('/')[2];
    if (presentDropdownItemPath) {
      setShowSuboptions('/account');
    }
    SetCurrentItem();
  }, [appState.currentPage]);

  function SetCurrentItem() {
    const presentTabPath = `/${window.location.pathname.split('/')[1]}`;
    const presentDropdownItemPath = `/${
      window.location.pathname.split('/')[2]
    }`;
    setCurrentNavItem(
      currentTabSelection(presentTabPath, presentDropdownItemPath)
    );
  }
  const handlePressEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      ref.current.click();
    }
  };

  const navigation = (
    <Navigation
      vertical
      value={currentNavItem}
      onChange={setCurrentNavItem}
      dataTestId="sidebar"
    >
      {OPTIONS.map(({ Icon, page, subOptions = [] }) => {
        const navigationItemLink = React.forwardRef((props, refer) => (
          <Link {...props} to={page.path} ref={refer} />
        ));
        navigationItemLink.displayName = 'NavigationItemLink';
        //Width handling for mobile devices
        const navItemWidth = width < 1024 ? '100%' : null;
        return (
          <NavigationItem
            style={{ width: navItemWidth }}
            component={navigationItemLink}
            icon={<Icon />}
            label={page.name}
            key={page.name}
            open={showSuboptions === page.path}
            onKeyDown={handlePressEnter}
            onClick={() => {
              if (showSuboptions !== page.path) {
                setShowSuboptions(page.path);
                setAppState((prevState) => ({
                  ...prevState,
                  currentPage: page.path + '_' + Date.now(),
                }));
              } else {
                setShowSuboptions('');
              }

              sendGAEvent(
                GACategory.navigation,
                `${user.roles} clicked on sidebar navigation item ${page.name}`
              );
              if (!subOptions?.length) hideSidebar();
            }}
          >
            {!!subOptions.length &&
              subOptions.map((option) => {
                const navigationSubItemLink = React.forwardRef(
                  (props, refer) => (
                    <Link {...props} to={option.path} ref={refer} />
                  )
                );
                navigationSubItemLink.displayName = 'NavigationSubItemLink';
                return (
                  <NavigationSubItem
                    component={navigationSubItemLink}
                    key={option.name}
                    label={option.name}
                    onKeyDown={handlePressEnter}
                    onClick={() => {
                      sendGAEvent(
                        GACategory.navigation,
                        `${user.roles} clicked on sidebar navigation item ${option.name}`
                      );
                      hideSidebar();
                    }}
                  />
                );
              })}
          </NavigationItem>
        );
      })}
    </Navigation>
  );

  return (
    <Box>
      <Hidden smDown>
        <Drawer anchor="left" variant="permanent" className={className}>
          {navigation}
        </Drawer>
      </Hidden>
      <Hidden mdUp>
        <Drawer
          anchor="left"
          open={show}
          onClose={(): void => hideSidebar()}
          variant="temporary"
          PaperProps={{ className }}
        >
          <DrawerHeader
            onClose={(): void => hideSidebar()}
            text="My Cambridge"
            divider
            dataTestId="drawerHeader"
          />
          {navigation}
        </Drawer>
      </Hidden>
    </Box>
  );
};

export default hot(memo(Sidebar));
